import { DateTime } from 'luxon';
import { roundToDecimal } from './helpers';
import { format, isValid } from 'date-fns';
import { isString } from './typeguards';

export enum TimeMeasure {
    MINUTE = 'MINUTE',
    HOUR = 'HOUR',
    DAY = 'DAY',
    WEEK = 'WEEK'
}

const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_MINUTE = 60;

const timeMeasureToSeconds = {
    [TimeMeasure.MINUTE]: SECONDS_IN_MINUTE,
    [TimeMeasure.HOUR]: SECONDS_IN_HOUR,
    [TimeMeasure.DAY]: SECONDS_IN_HOUR * 24,
    [TimeMeasure.WEEK]: SECONDS_IN_HOUR * 24 * 7
};

const timeMeasureToMinutes = {
    [TimeMeasure.MINUTE]: 1,
    [TimeMeasure.HOUR]: 60,
    [TimeMeasure.DAY]: 1440,
    [TimeMeasure.WEEK]: 10080
};

export function getSecondsToTimeUnits(value: number, to: TimeMeasure) {
    return value / timeMeasureToSeconds[to];
}

export function getTimeUnitsToSeconds(value: number, from: TimeMeasure) {
    return value * timeMeasureToSeconds[from];
}

export function getTimeUnitsToMinutes(value: number, from: TimeMeasure) {
    return (value * timeMeasureToSeconds[from]) / timeMeasureToSeconds[TimeMeasure.MINUTE];
}

export function getSecondsToDaysHours(value: number) {
    const days = Math.floor(roundToDecimal(value / timeMeasureToSeconds[TimeMeasure.DAY], 2));
    const remainingSeconds = value % timeMeasureToSeconds[TimeMeasure.DAY];
    if (remainingSeconds === 0) {
        return `${days} days`;
    }
    return `${days} days ${Math.round(remainingSeconds / timeMeasureToSeconds[TimeMeasure.HOUR])} hours`;
}

export function getSecondsToExpiryComponents(value: number) {
    const weeks = Math.floor(roundToDecimal(value / timeMeasureToSeconds[TimeMeasure.WEEK], 2));
    if (weeks > 0 && value % timeMeasureToSeconds[TimeMeasure.WEEK] === 0) {
        return { value: weeks, measure: TimeMeasure.WEEK };
    }
    const days = Math.floor(roundToDecimal(value / timeMeasureToSeconds[TimeMeasure.DAY], 2));
    const remainingSeconds = value % timeMeasureToSeconds[TimeMeasure.DAY];
    if (days > 0 && value % timeMeasureToSeconds[TimeMeasure.DAY] === 0) {
        return { value: days, measure: TimeMeasure.DAY };
    }
    return {
        value: Math.round(remainingSeconds / timeMeasureToSeconds[TimeMeasure.HOUR]),
        measure: TimeMeasure.HOUR
    };
}

export function getMinutesToExpiryComponents(value: number) {
    const weeks = Math.floor(roundToDecimal(value / timeMeasureToMinutes[TimeMeasure.WEEK], 2));
    if (weeks > 0 && value % timeMeasureToMinutes[TimeMeasure.WEEK] === 0) {
        return { value: weeks, measure: TimeMeasure.WEEK };
    }
    const days = Math.floor(roundToDecimal(value / timeMeasureToMinutes[TimeMeasure.DAY], 2));
    if (days > 0 && value % timeMeasureToMinutes[TimeMeasure.DAY] === 0) {
        return { value: days, measure: TimeMeasure.DAY };
    }
    const hours = Math.floor(roundToDecimal(value / timeMeasureToMinutes[TimeMeasure.HOUR], 2));
    if (hours > 0 && value % timeMeasureToMinutes[TimeMeasure.HOUR] === 0) {
        return { value: hours, measure: TimeMeasure.HOUR };
    }
    const minutes = Math.floor(roundToDecimal(value / timeMeasureToMinutes[TimeMeasure.MINUTE], 2));
    return { value: minutes, measure: TimeMeasure.MINUTE };
}

export const getNormalizedDateTime = (date: string | Date, merchantTimezone?: string): DateTime =>
    DateTime.fromJSDate(typeof date === 'string' ? new Date(date) : date).setZone(merchantTimezone || 'utc');

const MINUTES_IN_DAY = 1440;

export const getDaysFromMinutes = (minutes?: number) => {
    if (!minutes) {
        return minutes;
    }
    return Math.round(minutes / MINUTES_IN_DAY);
};
export const getMinutesFromDays = (days: number) => {
    if (!days) {
        return days;
    }
    return days * MINUTES_IN_DAY;
};

export const formatDateTime = (date: string | Date) => {
    if (isValid(isString(date) ? new Date(date) : date)) {
        return format(new Date(date), 'dd/MM/yyyy h:mmaa');
    }
};

export const formatDate = (date: string | Date) => {
    if (isValid(isString(date) ? new Date(date) : date)) {
        return format(new Date(date), 'dd/MM/yyyy');
    }
};
